import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text, Card, Button } from 'rebass/styled-components';
import { Input } from '@rebass/forms/styled-components';
import { adminApi, ManagedUser, Role, ROLES, roleRank, parseClientNames } from 'util/adminApi';
import { SUGGEST } from 'components/FieldSuggestions';
import { ui } from 'util/ui';

/** Choose the client/agency names a client login can see (task #1). Suggests existing spellings. */
const ClientPicker: FC<{ names: string[]; onChange(names: string[]): void }> = ({ names, onChange }) => {
  const [text, setText] = useState('');
  const add = () => {
    const next = parseClientNames([...names, text].join(','));
    onChange(next);
    setText('');
  };
  return (
    <Box mt={2}>
      <Text fontSize={0} color="grey3" mb={1}>
        Client or agency names this login can see. Projects match if their Client or Agency is any of these. Add
        every spelling in use, e.g. One3six, One3six Creative and One3Six Agency.
      </Text>
      <Flex sx={{ gap: '6px' }} flexWrap="wrap" alignItems="center">
        {names.map((n) => (
          <Text
            key={n}
            as="span"
            fontSize={0}
            px={2}
            py={1}
            bg="grey1"
            color="greyDark"
            sx={{ borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            {n}
            <button
              type="button"
              aria-label={`Remove ${n}`}
              onClick={() => onChange(names.filter((x) => x !== n))}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}
            >
              ×
            </button>
          </Text>
        ))}
        <Input
          placeholder="Start typing a client or agency…"
          value={text}
          list={SUGGEST.org}
          autoComplete="off"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          sx={{ flex: 1, minWidth: 220, fontSize: 1 }}
        />
        <SmallButton onClick={add} disabled={!text.trim()}>
          Add
        </SmallButton>
      </Flex>
    </Box>
  );
};

const previewHref = (names: string[], masters: boolean) =>
  `/portal?as=${encodeURIComponent(names.join('|'))}${masters ? '&masters=1' : ''}`;

/** Users tab, backed by the Phase 1 server functions: real sign-in status and controls. */

const Pill: FC<{ color: string }> = ({ color, children }) => (
  <Text
    as="span"
    fontSize={0}
    px={2}
    py="2px"
    color="white"
    bg={color}
    sx={{ borderRadius: 10, display: 'inline-block', whiteSpace: 'nowrap' }}
  >
    {children}
  </Text>
);

const selectStyle: React.CSSProperties = {
  padding: '4px 6px',
  border: '1px solid #D5D5D5',
  borderRadius: 4,
  background: 'white',
  fontSize: 14,
};

const SmallButton: FC<{ onClick(): void; disabled?: boolean; tone?: 'primary' | 'plain' | 'danger' }> = ({
  onClick,
  disabled,
  tone = 'plain',
  children,
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    py={1}
    px={2}
    mr={1}
    fontSize={0}
    sx={{
      bg: tone === 'primary' ? 'primary' : 'white',
      color: tone === 'primary' ? 'white' : tone === 'danger' ? '#B3261E' : 'greyDark',
      border: '1px solid',
      borderColor: tone === 'primary' ? 'primary' : tone === 'danger' ? '#E8B4B0' : 'grey2',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {children}
  </Button>
);

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never');

type Filter = 'all' | 'active' | 'disabled';

const LinkBox: FC<{ title: string; link: string; onClose(): void }> = ({ title, link, onClose }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Card p={3} mb={3} bg="white" sx={{ borderLeft: '4px solid', borderColor: 'green', borderRadius: 4, boxShadow: 'main' }}>
      <Text fontSize={1} fontWeight={1} color="greyDark" mb={1}>
        {title}
      </Text>
      <Text fontSize={0} color="grey3" mb={2}>
        Send this link to them yourself. It lets them choose a password, works once, and expires in about an hour.
      </Text>
      <Flex sx={{ gap: '8px' }} alignItems="center">
        <Input readOnly value={link} onFocus={(e: any) => e.target.select()} sx={{ fontSize: 0, flex: 1 }} />
        <SmallButton
          tone="primary"
          onClick={() => {
            navigator.clipboard.writeText(link).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </SmallButton>
        <SmallButton onClick={onClose}>Done</SmallButton>
      </Flex>
    </Card>
  );
};

const UsersManager: FC<{ currentUid?: string; currentRole: Role | null }> = ({ currentUid, currentRole }) => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [orphans, setOrphans] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [link, setLink] = useState<{ title: string; link: string } | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('member');
  const [newClients, setNewClients] = useState<string[]>([]);
  const [newMasters, setNewMasters] = useState(false);

  const isOwner = currentRole === 'owner';
  const myRank = roleRank(currentRole);
  const canManage = (u: ManagedUser) => u.uid !== currentUid && (isOwner || roleRank(u.role) < myRank);
  const grantable = ROLES.filter((r) => isOwner || roleRank(r.value) < myRank);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listUsers();
      setUsers(res.users.sort((a, b) => (a.email || '').localeCompare(b.email || '')));
      setOrphans(res.orphanProfiles.length);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const toggleDisabled = (u: ManagedUser) => {
    const verb = u.disabled ? 'Turn sign-in back on for' : 'Turn off sign-in for';
    if (!window.confirm(`${verb} ${u.email}?${u.disabled ? '' : ' They will be signed out.'}`)) return;
    run(u.uid, () => adminApi.setDisabled(u.uid, !u.disabled));
  };

  /** Ask which names a client login sees, and whether it can download masters. null = cancelled. */
  const askClientAccess = (u: ManagedUser) => {
    const typed = window.prompt(
      `Which client or agency names can ${u.email} see?\nSeparate several with commas, e.g. One3six, One3six Creative`,
      (u.clients || []).join(', '),
    );
    if (typed === null) return null;
    const clients = parseClientNames(typed);
    if (!clients.length) {
      setError('A client login needs at least one client or agency name.');
      return null;
    }
    const clientMasters = window.confirm(
      `Can ${u.email} download master files too?\n\nOK = yes, masters as well.\nCancel = previews and other files only (masters show as "on request").`,
    );
    return { clients, clientMasters };
  };

  const changeRole = (u: ManagedUser, value: string) => {
    const role = value === '' ? null : (value as Role);
    const label = role ? ROLES.find((r) => r.value === role)!.label : 'no access';
    if (role === 'client') {
      const access = askClientAccess(u);
      if (!access) return;
      if (!window.confirm(`Make ${u.email} a client login for ${access.clients.join(', ')}? They will be signed out.`)) return;
      run(u.uid, () => adminApi.setRole(u.uid, role, access));
      return;
    }
    if (!window.confirm(`Change ${u.email} to ${label}?`)) return;
    run(u.uid, () => adminApi.setRole(u.uid, role));
  };

  const editClientAccess = (u: ManagedUser) => {
    const access = askClientAccess(u);
    if (!access) return;
    run(u.uid, () => adminApi.setRole(u.uid, 'client', access));
  };

  const deleteUser = (u: ManagedUser) => {
    const typed = window.prompt(
      `Delete ${u.email} for good?\n\nThis removes their sign-in. It can't be undone, though they can be invited again later. ` +
        `Projects and files they added are not touched.\n\nType their email to confirm:`,
    );
    if (typed === null) return;
    run(u.uid, () => adminApi.deleteUser(u.uid, typed));
  };

  const resetLink = (u: ManagedUser) =>
    run(u.uid, async () => {
      const res = await adminApi.resetLink(u.uid);
      setLink({ title: `Password reset link for ${res.email}`, link: res.link });
    });

  const create = () =>
    run('create', async () => {
      const res = await adminApi.createUser(
        newEmail.trim(),
        newRole,
        newRole === 'client' ? { clients: newClients, clientMasters: newMasters } : undefined,
      );
      setNewEmail('');
      setNewClients([]);
      setNewMasters(false);
      setLink({ title: `Account created for ${res.email}`, link: res.setupLink });
    });

  const counts = useMemo(
    () => ({
      active: users.filter((u) => !u.disabled).length,
      disabled: users.filter((u) => u.disabled).length,
      noRole: users.filter((u) => !u.disabled && !u.role).length,
    }),
    [users],
  );
  const shown = users.filter((u) => filter === 'all' || (filter === 'active' ? !u.disabled : u.disabled));

  return (
    <Box mb={4}>
      <Flex alignItems="baseline" mb={2} sx={{ gap: '12px' }} flexWrap="wrap">
        <Text fontSize={3} fontWeight={1} color="greyDark">
          Users
        </Text>
        <Text fontSize={0} color="grey3">
          {counts.active} active · {counts.disabled} disabled
          {counts.noRole > 0 && ` · ${counts.noRole} active with no role (can't see anything)`}
        </Text>
        <Box flex={1} />
        {/* Three equal-width options in one control (Aaron, 5 Nov 2026). */}
        <div
          role="group"
          aria-label="Show users"
          style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, 88px)', gap: 4, padding: 4, background: ui.subtle, borderRadius: 12 }}
        >
          {(['all', 'active', 'disabled'] as Filter[]).map((f) => {
            const on = filter === f;
            return (
              <button
                key={f}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(f)}
                style={{
                  height: 32,
                  border: 'none',
                  borderRadius: 9,
                  background: on ? ui.surface : 'transparent',
                  boxShadow: on ? '0 1px 3px rgba(0,0,0,0.14)' : 'none',
                  color: on ? ui.ink : ui.muted,
                  font: 'inherit',
                  fontFamily: ui.font,
                  fontSize: 13,
                  fontWeight: on ? 600 : 500,
                  cursor: 'pointer',
                }}
              >
                {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Disabled'}
              </button>
            );
          })}
        </div>
      </Flex>

      {error && (
        <Card p={3} mb={3} bg="white" sx={{ borderLeft: '4px solid', borderColor: 'primary' }}>
          <Text color="primary" fontSize={1}>
            {error}
          </Text>
        </Card>
      )}
      {link && <LinkBox title={link.title} link={link.link} onClose={() => setLink(null)} />}

      <Card p={3} mb={3} bg="white" sx={{ boxShadow: 'main', borderRadius: 4 }}>
        <Text fontSize={1} fontWeight={1} color="greyDark" mb={2}>
          Add a user
        </Text>
        <Flex sx={{ gap: '8px' }} alignItems="center" flexWrap="wrap">
          <Input
            placeholder="name@company.com"
            value={newEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
            sx={{ flex: 1, minWidth: 220, fontSize: 1 }}
          />
          <select style={selectStyle} value={newRole} onChange={(e) => setNewRole(e.target.value as Role)}>
            {grantable.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <SmallButton
            tone="primary"
            onClick={create}
            disabled={!newEmail.includes('@') || busy === 'create' || (newRole === 'client' && newClients.length === 0)}
          >
            {busy === 'create' ? 'Creating…' : 'Create account'}
          </SmallButton>
        </Flex>
        <Text fontSize={0} color="grey3" mt={2}>
          {ROLES.find((r) => r.value === newRole)?.help}. No password is set; you get a link to send them.
        </Text>
        {newRole === 'client' && (
          <>
            <ClientPicker names={newClients} onChange={setNewClients} />
            <Flex mt={2} alignItems="center" sx={{ gap: '8px' }} flexWrap="wrap">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input type="checkbox" checked={newMasters} onChange={(e) => setNewMasters(e.target.checked)} />
                Can download master files (otherwise masters show as "on request")
              </label>
              {newClients.length > 0 && (
                <a href={previewHref(newClients, newMasters)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14 }}>
                  Preview what they'll see →
                </a>
              )}
            </Flex>
          </>
        )}
      </Card>

      <Card p={2} bg="white" sx={{ boxShadow: 'main', borderRadius: 4, overflowX: 'auto' }}>
        <Flex py={2} px={2} fontSize={0} color="grey3" sx={{ borderBottom: '1px solid', borderColor: 'grey2', minWidth: 720 }}>
          <Box flex={3}>Email</Box>
          <Box flex={1.4}>Role</Box>
          <Box flex={1}>Status</Box>
          <Box flex={1.2}>Last sign-in</Box>
          <Box flex={2}>Actions</Box>
        </Flex>
        {loading && !users.length && (
          <Text p={2} fontSize={1} color="grey3">
            Loading accounts…
          </Text>
        )}
        {shown.map((u) => {
          const manageable = canManage(u);
          return (
            <Flex
              key={u.uid}
              py={2}
              px={2}
              alignItems="center"
              fontSize={1}
              color="greyDark"
              sx={{ borderBottom: '1px solid', borderColor: 'grey1', minWidth: 720, opacity: busy === u.uid ? 0.5 : 1 }}
            >
              <Box flex={3} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.email || '(no email)'}
                {u.uid === currentUid && (
                  <Text as="span" color="grey3" fontSize={0}>
                    {'  '}(you)
                  </Text>
                )}
                {u.legacyAdmin && !u.role && (
                  <Text as="span" color="grey3" fontSize={0}>
                    {'  '}old admin flag
                  </Text>
                )}
              </Box>
              <Box flex={1.4}>
                <select
                  style={selectStyle}
                  value={u.role || ''}
                  disabled={!manageable || busy !== null}
                  onChange={(e) => changeRole(u, e.target.value)}
                >
                  <option value="">No access</option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value} disabled={!grantable.some((g) => g.value === r.value)}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {u.role === 'client' && (
                  <Box fontSize={0} color="grey3" mt={1}>
                    {(u.clients || []).join(', ') || 'no names set'}
                    {u.clientMasters ? ' · masters' : ''}
                    <Box>
                      {manageable && (
                        <button
                          type="button"
                          onClick={() => editClientAccess(u)}
                          disabled={busy !== null}
                          style={{ border: 'none', background: 'none', padding: 0, color: '#B8045A', cursor: 'pointer', fontSize: 12 }}
                        >
                          Edit names
                        </button>
                      )}
                      {' · '}
                      <a href={previewHref(u.clients || [], !!u.clientMasters)} target="_blank" rel="noopener noreferrer">
                        Preview
                      </a>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box flex={1}>{u.disabled ? <Pill color="grey3">Disabled</Pill> : <Pill color="green">Active</Pill>}</Box>
              <Box flex={1.2} fontSize={0} color="grey3">
                {when(u.lastSignInAt)}
              </Box>
              <Box flex={2}>
                <SmallButton onClick={() => toggleDisabled(u)} disabled={!manageable || busy !== null}>
                  {u.disabled ? 'Enable' : 'Disable'}
                </SmallButton>
                <SmallButton onClick={() => resetLink(u)} disabled={!manageable || busy !== null || !u.email}>
                  Reset link
                </SmallButton>
                {u.disabled && (
                  <SmallButton onClick={() => deleteUser(u)} disabled={!manageable || busy !== null} tone="danger">
                    Delete
                  </SmallButton>
                )}
              </Box>
            </Flex>
          );
        })}
      </Card>
      <Text fontSize={0} color="grey3" mt={2}>
        Disabling an account signs it out and blocks sign-in; nothing is deleted. Roles take effect at the person's next
        sign-in, or straight away when lowered.
        {orphans > 0 && ` ${orphans} profile record(s) have no sign-in account behind them (left over from removed accounts).`}
      </Text>
    </Box>
  );
};

export default UsersManager;
